import { Store } from '@website/Store';
import Avatar from '@website/components/directoryTheme/Avatar';
import Truncate from '@website/components/utilities/Truncate';
import { UserBookingDetailsQuery } from '@website/types/graphqlOperations';
import Link from 'next/link';
import { useContext } from 'react';
import { Badge, Button, Col, Row } from 'react-bootstrap';
import { MdChat } from 'react-icons/md';
import InviteGuest from './utils/InviteGuest';
import RemoveUserAsGuest from './utils/RemoveUserAsGuest';

/**
 * Individual Guest Space Card
 *
 * @returns Includes the guest name if set, and a button to add/edit the guest & trip form.
 */
export function IndividualGuestCard({
  space,
  guest,
  guestNumber,
  isInFuture,
  userID,
}: {
  space: Pick<UserBookingDetailsQuery['booking']['spaces'][0], 'id'>;
  guest: UserBookingDetailsQuery['booking']['spaces'][0]['guest'];
  guestNumber: number;
  isInFuture: boolean;
  /* Booking owner UserID */
  userID: string;
}) {
  const name = guest?.givenName
    ? `${guest?.givenName} ${guest?.familyName}`
    : `Space ${guestNumber}`;

  const { state } = useContext(Store);
  const isAdmin = state.user?.roles.includes('admin');

  return (
    <Row>
      <Col className="d-flex align-items-top mb-3">
        <div className="me-3">
          <Avatar
            size="lg"
            image={guest?.user?.picture?.file.key ?? guest?.picture?.file?.key}
            defaultImageText={guest?.givenName || guestNumber.toString()}
            defaultBackgroundColor="grey"
            border
            alt={guest?.givenName || guestNumber.toString()}
          />
        </div>

        <div>
          <h6 className="d-inline align-middle">{name}</h6>
          <Badge
            pill
            bg={guest?.completed ? 'success' : 'danger'}
            className="ms-2 align-middle"
          >
            <small>{guest?.completed ? 'Complete' : 'Trip form due!'}</small>
          </Badge>

          <div className="mt-3">
            {(isAdmin || isInFuture) && (
              <>
                {/*
                 * Hide edit button for booking owner if the guest is a different user.
                 * When there is no guest or userID is same as the booking owner.
                 */}
                {(!guest.userID || guest.userID === userID) && (
                  <Link
                    href={`/me/${userID}/guestForm/${space.id}`}
                    legacyBehavior
                  >
                    <Button size="sm" className="me-1 mb-1">
                      {guest?.givenName ? 'Edit trip form' : 'Add trip form'}
                    </Button>
                  </Link>
                )}
                {/* if guest has claimed a space, do not show invite UI */}
                {!guest.userID && !guest.givenName && (
                  <InviteGuest
                    accessCode={guest.accessCode}
                    spaceID={space.id}
                  />
                )}
                {/* if guest has claimed a space, show button to remove UI */}
                {(guest.userID || guest.givenName) && (
                  <RemoveUserAsGuest spaceID={space.id} />
                )}
              </>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
}

/**
 * Filter to only guests that aren't part of this booking/guest
 */
export function filterToOtherGuestsOnly(
  guests: UserBookingDetailsQuery['booking']['group']['guests'],
  yourSpaces: Array<
    Pick<UserBookingDetailsQuery['booking']['spaces'][0], 'guest'>
  >,
): UserBookingDetailsQuery['booking']['group']['guests']['items'] {
  const yourGuestIDs = yourSpaces
    // remove any guest that's undefined or null, mostly from cancelled spaces
    .filter((space) => space.guest)
    .map((space) => space.guest.id);

  const otherGuests = guests.items.filter((guest) => {
    if (yourGuestIDs.includes(guest.id)) return false;
    return true;
  });

  return otherGuests;
}

/**
 * User Booking Details - Guests
 *
 * Shows each guest space - with the option to add/edit details for each one.
 */
export default function Guests({
  spaces,
  group,
  isInFuture,
  userID,
  hideSocialProof = false,
}: {
  spaces: Array<
    Pick<
      UserBookingDetailsQuery['booking']['spaces'][0],
      'id' | 'active' | 'guest'
    >
  >;
  group: UserBookingDetailsQuery['booking']['group'];
  isInFuture: boolean;
  userID: string;
  hideSocialProof?: boolean;
}) {
  const activeSpaces = spaces.filter((sp) => sp.active);

  // Get any guests that are not on this booking
  const otherGuests = filterToOtherGuestsOnly(group.guests, activeSpaces);
  const otherGuestsWithNames = otherGuests.filter(
    (i) => !!i.givenName || !!i.user?.givenName,
  );

  if (activeSpaces.length === 0) {
    return null;
  }

  return (
    <div className="text-block">
      <div className="mb-3">
        <h5>Your team</h5>

        <h6 className="mt-4 mb-3">
          You have{' '}
          {activeSpaces.length > 1
            ? `${activeSpaces.length} spaces`
            : '1 space'}
        </h6>

        {activeSpaces.map((space, key) => (
          <IndividualGuestCard
            space={space}
            guest={space.guest}
            key={space.id}
            guestNumber={key + 1}
            isInFuture={isInFuture}
            userID={userID}
          />
        ))}

        {!hideSocialProof && otherGuests.length >= 1 && (
          <>
            <h6 className="mt-5 mb-3">
              There
              {otherGuests.length > 1
                ? ` are ${otherGuests.length} other people on your trip`
                : ' is 1 other person on your trip'}
            </h6>

            {otherGuests.length > 1 && (
              <>
                <p className="text-muted small">
                  We'll show you everyone with profiles here:
                </p>

                {otherGuestsWithNames.map((guest) => (
                  <Row key={guest.id}>
                    <Col className="d-flex align-items-middle">
                      <div className="me-2">
                        <Avatar
                          size="sm"
                          image={
                            guest.user?.picture?.file.key ||
                            guest.picture?.file?.key
                          }
                          defaultImageText={
                            guest.givenName || guest.user?.givenName
                          }
                          border
                          alt={guest.givenName || guest.user?.givenName}
                        />
                      </div>

                      <div className="align-items-center">
                        <p className="mt-1 mb-0">
                          <strong>
                            {guest.givenName || guest.user?.givenName}
                          </strong>
                        </p>
                        {guest.user?.bio && (
                          <Truncate
                            lines={1}
                            enableExpand
                            className="small text-muted my-1"
                          >
                            {guest.user.bio}
                          </Truncate>
                        )}
                      </div>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </>
        )}

        {!hideSocialProof && (
          <Link
            passHref
            href={`/me/${userID}/messages/${group.id}`}
            legacyBehavior
          >
            <Button as="a" className="mb-3 me-2 mt-4">
              <label className="my-0 me-2">
                <MdChat size="1.4em" />
              </label>
              Chat with other guests
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
